import React, { useState, useEffect } from "react";
import {
  Select,
  MenuItem,
  TextField,
  Autocomplete,
  FormControl,
  InputLabel,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClearIcon from "@mui/icons-material/Clear";
import styles from "./styles.module.css";

const opcoesColunas = [
  { value: "descricaoConta", label: "Descrição da Conta" },
  { value: "tipoContaContabil", label: "Tipo de Conta" },
  { value: "codigoEcd", label: "Código ECD" },
  { value: "descricaoLancamento", label: "Descrição do Lançamento" },
  { value: "valorTotal", label: "Valor Total" },
  { value: "pis", label: "PIS" },
  { value: "cofins", label: "COFINS" },
  { value: "compoeProjeto", label: "Compõe Projeto" },
  { value: "baseLegal", label: "Base Legal" },
];

const FiltroTabelaPisCofins = ({
  colunaSelecionada = "",
  valorFiltro = "",
  basesLegais = [],
  onColunaChange,
  onValorChange,
  onFiltrar,
  onLimpar,
  loading = false,
  colunasExcluidas = [], // Array de valores de colunas a serem excluídas (ex: ["compoeProjeto"])
}) => {
  const [colunaLocal, setColunaLocal] = useState(colunaSelecionada ?? "");
  const [valorLocal, setValorLocal] = useState(
    valorFiltro === undefined || valorFiltro === null ? "" : String(valorFiltro),
  );

  // Filtrar colunas disponíveis excluindo as especificadas
  const colunasDisponiveis = opcoesColunas.filter(
    (coluna) => !colunasExcluidas.includes(coluna.value)
  );

  // Sincronizar apenas quando os estados externos forem limpos (quando clicar em "Limpar")
  useEffect(() => {
    if (!colunaSelecionada) {
      setColunaLocal("");
    }
  }, [colunaSelecionada]);

  // Limpar seleção se a coluna selecionada estiver na lista de excluídas
  useEffect(() => {
    if (colunaLocal && colunasExcluidas.includes(colunaLocal)) {
      setColunaLocal("");
      setValorLocal("");
      onColunaChange?.("");
      onValorChange?.("");
    }
  }, [colunasExcluidas]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (valorFiltro === "" || valorFiltro === null || valorFiltro === undefined) {
      setValorLocal("");
    }
  }, [valorFiltro]);

  const handleColunaChange = (event) => {
    const novaColuna = event.target.value;
    setColunaLocal(novaColuna);
    setValorLocal(""); // Limpar valor ao mudar coluna
    // Não atualiza o filtro até clicar em "Filtrar"
  };

  const handleValorChange = (novoValor) => {
    setValorLocal(novoValor);
    // Não atualiza o filtro até clicar em "Filtrar"
  };

  const handleFiltrar = () => {
    // Aplicar os valores locais aos estados de filtro apenas quando clicar
    onColunaChange?.(colunaLocal);
    onValorChange?.(valorLocal);
    onFiltrar?.(colunaLocal, valorLocal);
  };

  const handleLimpar = () => {
    setColunaLocal("");
    setValorLocal("");
    onColunaChange?.("");
    onValorChange?.("");
    onLimpar?.();
  };

  const renderCampoFiltro = () => {
    if (!colunaLocal) {
      return (
        <TextField
          size="small"
          placeholder="Selecione uma coluna primeiro"
          value=""
          disabled
          className={styles.campoFiltro}
        />
      );
    }

    // Base Legal - Autocomplete
    if (colunaLocal === "baseLegal") {
      return (
        <Autocomplete
          size="small"
          options={basesLegais}
          getOptionLabel={(option) => option.descricao || ""}
          value={
            basesLegais.find((b) => b.id === valorLocal) || null
          }
          onChange={(_, novoValor) => {
            handleValorChange(novoValor?.id || null);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Selecione a base legal"
              className={styles.campoFiltro}
            />
          )}
          disabled={loading}
        />
      );
    }

    // Compõe Projeto - Select (Sim/Não/Todos)
    if (colunaLocal === "compoeProjeto") {
      return (
        <Select
          size="small"
          value={valorLocal !== "" ? valorLocal : "todos"}
          onChange={(e) => {
            const valor = e.target.value === "todos" ? null : e.target.value === "true";
            handleValorChange(valor);
          }}
          className={styles.campoFiltro}
          disabled={loading}
        >
          <MenuItem value="todos">Todos</MenuItem>
          <MenuItem value="true">Sim</MenuItem>
          <MenuItem value="false">Não</MenuItem>
        </Select>
      );
    }

    // Valor Total, PIS, COFINS - Campo numérico
    if (["valorTotal", "pis", "cofins"].includes(colunaLocal)) {
      return (
        <TextField
          size="small"
          type="number"
          placeholder="Digite o valor"
          value={valorLocal == null ? "" : String(valorLocal)}
          onChange={(e) => handleValorChange(e.target.value)}
          className={styles.campoFiltro}
          disabled={loading}
          inputProps={{
            step: "0.01",
            min: 0,
          }}
        />
      );
    }

    // Demais colunas - Campo de texto
    return (
      <TextField
        size="small"
        placeholder={`Digite o ${opcoesColunas.find((c) => c.value === colunaLocal)?.label?.toLowerCase() || "valor"}`}
        value={valorLocal == null ? "" : String(valorLocal)}
        onChange={(e) => handleValorChange(e.target.value)}
        className={styles.campoFiltro}
        disabled={loading}
      />
    );
  };

  return (
    <div className={styles.filtroContainer}>
      <FormControl 
        size="small" 
        className={styles.selectColuna}
        sx={{ minWidth: 250, width: 250 }}
      >
        <InputLabel>Coluna</InputLabel>
        <Select
          value={colunaLocal ?? ""}
          onChange={handleColunaChange}
          label="Coluna"
          disabled={loading}
          sx={{ minWidth: 250 }}
        >
          <MenuItem value="">
            <em>Selecione uma coluna</em>
          </MenuItem>
          {colunasDisponiveis.map((coluna) => (
            <MenuItem key={coluna.value} value={coluna.value}>
              {coluna.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <div className={styles.campoWrapper}>
        {renderCampoFiltro()}
      </div>

      <button
        type="button"
        onClick={handleFiltrar}
        disabled={!colunaLocal || loading}
        className={!colunaLocal || loading ? styles.disabledButton : styles.botaoFiltrar}
      >
        <FilterListIcon style={{ fontSize: "1rem", marginRight: "0.5rem" }} />
        <span>Filtrar</span>
      </button>

      {(colunaLocal || valorLocal) && (
        <button
          type="button"
          onClick={handleLimpar}
          disabled={loading}
          className={loading ? styles.disabledButton : styles.botaoLimpar}
        >
          <ClearIcon style={{ fontSize: "1rem" }} />
          <span>Limpar</span>
        </button>
      )}
    </div>
  );
};

export default FiltroTabelaPisCofins;

